import type { MinimapFiles, OfflineError } from "@novorender/api";

import { telemetry } from "telemetry";
import { log } from "utils/log";

import type { MiniMapPayload } from "../minimapSlice";
import { minimapSyncPruneStore, pruneStaleMinimapFiles } from "./offlinePrune";
import { minimapFilePath } from "./offlineStore";
import { buildTileSetFileUrl, parseTileSetManifest, TILE_MANIFEST_RELATIVE_PATH } from "./tiledSource";
import { allTileSetFilePaths, type TiledDrawingSource, tileSetFileCount } from "./tileMath";

const l = log.miniMap.tiled;

/** Concurrent download cap, matching the online tile fetch's own budget (`tileBitmapCache.ts`). */
const MAX_CONCURRENT_DOWNLOADS = 8;
/** Arbitrary: enough to ride out a brief network blip without stalling the sync on a dead file. */
const RETRY_BACKOFF_MS = [1000, 4000];

/**
 * Where a full sync's mini map download reads and writes: the folder the SDK
 * hands the sync's `syncMinimap` callback, scoped to `minimap/` and to that
 * sync, plus a read of a file already on disk (the folder has none; reading
 * the scene directory is not a change to it).
 */
export type MinimapFullSyncFolder = Pick<MinimapFiles, "list" | "write" | "reportProgress"> & {
    read(name: string): Promise<ArrayBuffer | undefined>;
};

/**
 * What a download left out. Anything here means the scene's mini map is
 * incomplete offline, which the caller reports to the SDK by rejecting.
 */
export type MinimapSyncReport = {
    /** Drawings with no completed tile-set import, or whose tile-set manifest couldn't be had. */
    readonly skippedDrawings: number;
    /** Files that exhausted their retries, whose write the storage refused, or whose name can't be stored. */
    readonly skippedFiles: number;
    /** The storage's `"quotaExceeded"`, which ended the download: the files after it weren't fetched. */
    readonly storageFull?: OfflineError;
};

/**
 * Resolve a drawing's tile-set root URL, or `null` when it has no completed
 * import yet. `forceRefresh` mints a fresh signed prefix instead of reusing a
 * cached one (same contract as `createBimSceneFileSignedDownloadUrl`) --
 * needed because a full sync's hundreds of tiles across 8 workers, running
 * after geometry sync already completed, are the case most likely to outlive
 * a FAS signature's lifetime.
 */
export type ResolveMinimapBaseUrl = (
    fileInstanceId: string,
    options?: { forceRefresh?: boolean },
) => Promise<string | null>;

/** One tile-set file still to download, as handed to a worker. */
type DownloadItem = { fileInstanceId: string; relativePath: string; baseUrl: string };

function delay(ms: number, abortSignal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, ms);
        abortSignal.addEventListener(
            "abort",
            () => {
                clearTimeout(timer);
                reject(abortSignal.reason as Error);
            },
            { once: true },
        );
    });
}

function throwIfAborted(abortSignal: AbortSignal): void {
    if (abortSignal.aborted) {
        throw (abortSignal.reason as Error | undefined) ?? new Error("Aborted");
    }
}

/**
 * Download every file of every listed drawing's tile set into the sync's
 * `minimap/` folder, skipping what is already stored. A drawing with no
 * completed import, or a file out of retries, is skipped and counted in the
 * report; only an abort propagates, so a pause doesn't read as finished.
 */
export async function syncMinimapFiles({
    folder,
    fileInstanceIds,
    resolveBaseUrl,
    abortSignal,
}: {
    folder: MinimapFullSyncFolder;
    fileInstanceIds: readonly string[];
    resolveBaseUrl: ResolveMinimapBaseUrl;
    abortSignal: AbortSignal;
}): Promise<MinimapSyncReport> {
    throwIfAborted(abortSignal);

    const existing = new Set<string>();
    for (const entry of await folder.list()) {
        existing.add(entry.name);
    }

    let skippedDrawings = 0;
    let skippedFiles = 0;

    // Resolve every drawing's manifest first (a ~1 KB document each), but
    // don't materialize the file list off them: a 20-sheet model is ~34,000
    // paths, several MB of strings held before a single byte is downloaded.
    // The generator below keeps only the current drawing's paths alive.
    const tileSets: { fileInstanceId: string; source: TiledDrawingSource; baseUrl: string }[] = [];
    for (const fileInstanceId of new Set(fileInstanceIds)) {
        throwIfAborted(abortSignal);
        const resolved = await resolveTileSetForFullSync(folder, fileInstanceId, resolveBaseUrl, existing, abortSignal);
        if (!resolved) {
            // No completed import yet, or resolution failed -- telemetry already recorded.
            skippedDrawings++;
            continue;
        }
        tileSets.push({ fileInstanceId, ...resolved });
    }

    function* pendingDownloads(onUnstorable?: () => void): Generator<DownloadItem> {
        for (const { fileInstanceId, source, baseUrl } of tileSets) {
            for (const relativePath of allTileSetFilePaths(source)) {
                const path = minimapFilePath(fileInstanceId, relativePath);
                if (!path) {
                    // Never storable, so never downloaded: the mini map stays without it.
                    onUnstorable?.();
                } else if (!existing.has(path)) {
                    yield { fileInstanceId, relativePath, baseUrl };
                }
            }
        }
    }

    // Counted by a throwaway pass rather than tracked while enumerating: the
    // total has to be post-dedupe for the progress bar to stay honest on a
    // resumed sync, and deciding that means building each path to test it
    // against `existing`. Pure string work, no IO, and still only one
    // drawing's paths resident at a time.
    let total = 0;
    const counter = pendingDownloads(() => skippedFiles++);
    while (!counter.next().done) {
        total++;
    }

    let completed = 0;
    folder.reportProgress(completed, total);
    if (total === 0) {
        return { skippedDrawings, skippedFiles };
    }

    const queue = pendingDownloads();
    let storageFull: OfflineError | undefined;
    async function worker(): Promise<void> {
        // A full device refuses every write after the first: fetching the rest would only
        // download what can't be kept.
        while (!storageFull) {
            throwIfAborted(abortSignal);
            // Pulled synchronously, so two workers can never be handed the
            // same item however their awaits interleave.
            const next = queue.next();
            if (next.done) {
                return;
            }
            const outcome = await downloadFileWithRetry(folder, next.value, resolveBaseUrl, abortSignal);
            if (outcome !== "stored") {
                skippedFiles++;
            }
            if (typeof outcome === "object") {
                storageFull ??= outcome.storageFull;
                return;
            }
            completed++;
            folder.reportProgress(completed, total);
        }
    }

    const workerCount = Math.min(MAX_CONCURRENT_DOWNLOADS, total);
    // `allSettled`, not `all`: `downloadFileWithRetry` only ever rejects via
    // `throwIfAborted` (it's best-effort otherwise), so every rejection here
    // is abort-shaped -- but `Promise.all` would settle on the FIRST one and
    // leave the other workers' later rejections unhandled (an
    // `unhandledrejection` in the WebView, plus writes racing past the point
    // the caller already decided "aborted"). Letting every worker actually
    // finish observing the same abort, then re-throwing once, keeps pause
    // deterministic.
    await Promise.allSettled(Array.from({ length: workerCount }, worker));
    throwIfAborted(abortSignal);
    return { skippedDrawings, skippedFiles, ...(storageFull && { storageFull }) };
}

/** Bytes assumed per tile-set file for a manifest without `tileBytes` and `previewBytes`. */
const ESTIMATED_BYTES_PER_FILE = 10_000;

/**
 * The bytes of the tile sets {@link syncMinimapFiles} covers, from their manifests' `tileBytes` and
 * `previewBytes` or estimated from their file count, for a full sync's progress: `SyncProgress` counts
 * no mini map bytes. A drawing with no completed import, or whose manifest can't be had, counts none, as the
 * download skips it.
 * @throws Only an abort.
 */
export async function estimateMinimapBytes({
    fileInstanceIds,
    resolveBaseUrl,
    abortSignal,
}: {
    fileInstanceIds: readonly string[];
    resolveBaseUrl: ResolveMinimapBaseUrl;
    abortSignal: AbortSignal;
}): Promise<number> {
    const queue = new Set(fileInstanceIds).values();
    let total = 0;
    async function worker(): Promise<void> {
        // Pulled synchronously, so two workers are never handed the same drawing.
        for (let next = queue.next(); !next.done; next = queue.next()) {
            // Added after the await: `total += await …` would add to the total read before it.
            const bytes = await estimateTileSetBytes(next.value, resolveBaseUrl, abortSignal);
            total += bytes;
        }
    }
    await Promise.all(Array.from({ length: MAX_CONCURRENT_DOWNLOADS }, worker));
    return total;
}

/** One drawing's share of {@link estimateMinimapBytes}. @throws Only an abort. */
async function estimateTileSetBytes(
    fileInstanceId: string,
    resolveBaseUrl: ResolveMinimapBaseUrl,
    abortSignal: AbortSignal,
): Promise<number> {
    try {
        const baseUrl = await resolveBaseUrl(fileInstanceId);
        if (!baseUrl) {
            return 0;
        }
        const manifestUrl = buildTileSetFileUrl(baseUrl, TILE_MANIFEST_RELATIVE_PATH);
        const response = await fetch(manifestUrl, { signal: abortSignal });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status} for ${manifestUrl}`);
        }
        const source = parseTileSetManifest(await response.json(), baseUrl, manifestUrl);
        return source.totalBytes ?? tileSetFileCount(source) * ESTIMATED_BYTES_PER_FILE;
    } catch {
        // The download's own resolve reports the failure; this only leaves it out.
        throwIfAborted(abortSignal);
        return 0;
    }
}

/**
 * The tile set for one drawing, from OPFS if already journaled, else resolved online and
 * teed into OPFS. `undefined` on any failure, or when the drawing has no completed import.
 * `baseUrl` is resolved even for a stored manifest, whose tiles may not all be (incremental
 * caching stores the viewed ones), and is `""` only for a tile set already whole.
 */
async function resolveTileSetForFullSync(
    folder: MinimapFullSyncFolder,
    fileInstanceId: string,
    resolveBaseUrl: ResolveMinimapBaseUrl,
    existing: ReadonlySet<string>,
    abortSignal: AbortSignal,
): Promise<{ source: TiledDrawingSource; baseUrl: string } | undefined> {
    const manifestPath = minimapFilePath(fileInstanceId, TILE_MANIFEST_RELATIVE_PATH);
    let cachedSource: TiledDrawingSource | undefined;

    if (manifestPath && existing.has(manifestPath)) {
        try {
            const buffer = await folder.read(manifestPath);
            if (buffer) {
                cachedSource = parseTileSetManifest(JSON.parse(new TextDecoder().decode(buffer)), "", manifestPath);
            }
        } catch (error) {
            l.warn("Failed to read cached minimap manifest, re-resolving online:", fileInstanceId, error);
        }
    }

    if (cachedSource) {
        const source = cachedSource;
        const allCached = allTileSetFilePaths(source).every((relativePath) => {
            const path = minimapFilePath(fileInstanceId, relativePath);
            // An unsafe path can never be downloaded either way -- don't let
            // it force a signed-URL resolve that would go unused.
            return path ? existing.has(path) : true;
        });
        if (allCached) {
            return { source, baseUrl: "" };
        }
    }

    const baseUrl = await resolveBaseUrl(fileInstanceId);
    if (!baseUrl) {
        l.debug("Skipping full-sync minimap download: no completed tile-set import yet:", fileInstanceId);
        return undefined;
    }

    if (cachedSource) {
        return { source: cachedSource, baseUrl };
    }

    const manifestUrl = buildTileSetFileUrl(baseUrl, TILE_MANIFEST_RELATIVE_PATH);
    try {
        const response = await fetch(manifestUrl, { signal: abortSignal });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status} for ${manifestUrl}`);
        }
        const text = await response.text();
        if (manifestPath) {
            // A failed tee means this manifest re-fetches next time. Not counted as
            // skipped: the SDK already marks the mini map failed for any write it refused.
            await folder.write(manifestPath, new TextEncoder().encode(text).buffer as ArrayBuffer).catch(() => {});
        }
        return { source: parseTileSetManifest(JSON.parse(text), baseUrl, manifestUrl), baseUrl };
    } catch (error) {
        // An abort mid-fetch must propagate, not be recorded as a resolution
        // failure and swallowed -- same reasoning as `downloadFileWithRetry`.
        throwIfAborted(abortSignal);
        telemetry.trackCaughtException({
            error,
            source: "miniMap.offlineSync.resolveTileSet",
            category: "data",
            severity: "warning",
            extraAttributes: { fileInstanceId },
        });
        return undefined;
    }
}

/**
 * Download a file, retrying the network, and store it.
 * @returns `"skipped"` once the download has exhausted its retries, or the storage refused the
 *   write; the refusal itself when it is `"quotaExceeded"`.
 */
async function downloadFileWithRetry(
    folder: MinimapFullSyncFolder,
    item: DownloadItem,
    resolveBaseUrl: ResolveMinimapBaseUrl,
    abortSignal: AbortSignal,
): Promise<"stored" | "skipped" | { storageFull: OfflineError }> {
    const path = minimapFilePath(item.fileInstanceId, item.relativePath);
    if (!path) {
        return "skipped";
    }
    const buffer = await downloadWithRetry(item, resolveBaseUrl, abortSignal);
    if (!buffer) {
        return "skipped";
    }
    try {
        await folder.write(path, buffer);
        return "stored";
    } catch (error) {
        throwIfAborted(abortSignal);
        // The SDK has already marked the mini map failed for the refused write; downloading the
        // file again wouldn't change the storage's answer.
        if (isOfflineError(error) && error.kind === "quotaExceeded") {
            return { storageFull: error };
        }
        telemetry.trackCaughtException({
            error,
            source: "miniMap.offlineSync.writeFile",
            category: "data",
            severity: "warning",
            extraAttributes: { fileInstanceId: item.fileInstanceId, relativePath: item.relativePath },
        });
        return "skipped";
    }
}

/** The SDK's typed failures, as `MinimapFiles` rejects with them: plain data. */
function isOfflineError(error: unknown): error is OfflineError {
    return typeof error === "object" && error !== null && "kind" in error && typeof error.kind === "string";
}

/** @returns The file's bytes; `undefined` once it has exhausted its retries. */
async function downloadWithRetry(
    item: DownloadItem,
    resolveBaseUrl: ResolveMinimapBaseUrl,
    abortSignal: AbortSignal,
): Promise<ArrayBuffer | undefined> {
    // Mutable, not `item.baseUrl` directly: a 401/403 mints a fresh prefix
    // below and later attempts (for this file) retry against it.
    let baseUrl = item.baseUrl;
    for (let attempt = 0; ; attempt++) {
        throwIfAborted(abortSignal);
        try {
            const response = await fetch(buildTileSetFileUrl(baseUrl, item.relativePath), { signal: abortSignal });
            if (response.status === 401 || response.status === 403) {
                // Same "signature can outlive the session" case the online
                // route handles (`fetchTileSetFile` + `refreshTileSetBaseUrl`)
                // -- a full sync's hundreds of tiles across 8 workers, running
                // after geometry sync already finished, are the case most
                // likely to actually hit it. `null` (refresh failed) falls
                // through to the normal retry against the same stale prefix,
                // which will keep failing the same way until retries exhaust.
                const refreshed = await resolveBaseUrl(item.fileInstanceId, { forceRefresh: true });
                if (refreshed) {
                    baseUrl = refreshed;
                }
            }
            if (!response.ok) {
                throw new Error(`HTTP ${response.status} for ${item.relativePath}`);
            }
            return await response.arrayBuffer();
        } catch (error) {
            // An abort mid-fetch (or on the final attempt) must propagate, not
            // be telemetried and swallowed as a routine best-effort skip --
            // that would silently turn a user-initiated pause into "this file
            // just failed" from the caller's point of view.
            throwIfAborted(abortSignal);
            if (attempt >= RETRY_BACKOFF_MS.length) {
                telemetry.trackCaughtException({
                    error,
                    source: "miniMap.offlineSync.downloadFile",
                    category: "data",
                    severity: "warning",
                    extraAttributes: { fileInstanceId: item.fileInstanceId, relativePath: item.relativePath },
                });
                return undefined;
            }
            await delay(RETRY_BACKOFF_MS[attempt], abortSignal);
        }
    }
}

/**
 * The body of a full sync's `syncMinimap` callback: download the model's drawings' tile sets
 * into the folder the SDK hands the callback, then prune the trees of drawings no longer in the
 * scene. Does what it can, then rejects if it left anything out, so the mini map reads
 * `"failed"` rather than `"synced"` with tiles missing; the first write refused for storage ends
 * it with `"quotaExceeded"`. A pause rejects with the abort reason.
 *
 * @param download Downloads the tile sets (see {@link syncMinimapFiles}); `undefined` when the
 *   drawing list couldn't be asked for, which fails the phase. `fileInstanceIds` is `undefined`
 *   for a list that can't say which tile sets are current (see {@link minimapFileInstanceIds}):
 *   then nothing was downloaded, nothing is pruned, and the phase completes.
 * @param currentFileInstanceIds The drawings the prune keeps, fetched again after the download to
 *   catch a remap landing mid-download; `undefined` when that fails, and the prune then keeps the
 *   ones just downloaded.
 */
export async function runMinimapSyncPhase({
    files,
    read,
    download,
    currentFileInstanceIds,
    signal,
}: {
    files: Pick<MinimapFiles, "list" | "write" | "remove" | "reportProgress">;
    read: MinimapFullSyncFolder["read"];
    download: (
        folder: MinimapFullSyncFolder,
        signal: AbortSignal,
    ) => Promise<{ fileInstanceIds: readonly string[] | undefined; report: MinimapSyncReport } | undefined>;
    currentFileInstanceIds: () => Promise<readonly string[] | undefined>;
    signal: AbortSignal;
}): Promise<void> {
    const synced = await download(
        {
            list: () => files.list(),
            write: (name, buffer) => files.write(name, buffer),
            reportProgress: (done, total) => files.reportProgress(done, total),
            read,
        },
        signal,
    );

    try {
        await pruneStaleMinimapFiles({
            store: minimapSyncPruneStore(files),
            currentFileInstanceIds: (await currentFileInstanceIds()) ?? synced?.fileInstanceIds,
            abortSignal: signal,
        });
    } catch (error) {
        if (signal.aborted) {
            throw error;
        }
        // Stale trees left behind don't make the mini map incomplete, and the next sync's
        // prune tries again. A removal the folder refused is already in the sync's mini map
        // outcome.
        telemetry.trackCaughtException({
            error,
            source: "miniMap.offlineSync.prune",
            category: "data",
            severity: "warning",
        });
    }

    if (!synced) {
        throw new Error("Mini map not downloaded: the model's drawings couldn't be determined");
    }
    const { skippedDrawings, skippedFiles, storageFull } = synced.report;
    if (storageFull) {
        throw storageFull;
    }
    if (skippedDrawings > 0 || skippedFiles > 0) {
        throw new Error(`Mini map incomplete: ${skippedDrawings} drawing(s) and ${skippedFiles} file(s) skipped`);
    }
}

/**
 * The tile-set ids in a mini map payload, or `undefined` when the payload can't say which tile
 * sets are current.
 *
 * Model Manager returns no sheets both for a Snapshot that genuinely has none and for one whose
 * slice predates `surviving_container_ids`, because it reads the missing field as an empty set
 * rather than failing open. The two look the same from here, and taking the second at face value
 * would have the prune delete every tile set a downloaded Snapshot holds — which only comes back by
 * re-downloading online, and the Download button exists only for Snapshots. So a Snapshot with no
 * mapped sheets at all means "unknown": nothing is pruned or downloaded. Safe to relax once Model
 * Manager fails open on the missing field. Mapped sheets that are only off for navigation are a
 * real answer, so their stale tile sets are still pruned (BM-14175).
 *
 * Drawings that carry no tile-set id at all mean "unknown" too: that is missing data, not every
 * tile set going stale.
 */
export function minimapFileInstanceIds(
    payload: MiniMapPayload,
    sceneSliceId: string | undefined,
): string[] | undefined {
    const drawings = Object.values(payload.drawings);
    if (drawings.length === 0) {
        return sceneSliceId && payload.mappedSheetCount === 0 ? undefined : [];
    }
    const fileInstanceIds = new Set<string>();
    for (const drawing of drawings) {
        if (drawing.drawingData.platformFileInstanceId) {
            fileInstanceIds.add(drawing.drawingData.platformFileInstanceId);
        }
    }
    return fileInstanceIds.size > 0 ? [...fileInstanceIds] : undefined;
}
